import { Controller } from "@hotwired/stimulus"
import { parseJsonScript } from "../utils/dom"

const IATA_CODE = /^[A-Z0-9]{2}$/

export default class extends Controller {
  static targets = ["searchInput", "list", "hiddenContainer", "tags", "airlinesData"]
  static values = {
    selected: { type: Array, default: [] },
    inputName: String
  }

  connect() {
    this.selectedValue = this.normalizeCodes(this.selectedValue)
    this.boundFilterAndShow = this.filterAndShow.bind(this)
    this.boundOnSearchKeydown = this.onSearchKeydown.bind(this)
    this.boundCloseOnClickOutside = this.closeOnClickOutside.bind(this)
    this.renderHiddenInputs()
    this.renderTags()
    this.searchInputTarget.addEventListener("input", this.boundFilterAndShow)
    this.searchInputTarget.addEventListener("focus", this.boundFilterAndShow)
    this.searchInputTarget.addEventListener("keydown", this.boundOnSearchKeydown)
    document.addEventListener("click", this.boundCloseOnClickOutside)
  }

  disconnect() {
    this.searchInputTarget.removeEventListener("input", this.boundFilterAndShow)
    this.searchInputTarget.removeEventListener("focus", this.boundFilterAndShow)
    this.searchInputTarget.removeEventListener("keydown", this.boundOnSearchKeydown)
    document.removeEventListener("click", this.boundCloseOnClickOutside)
  }

  get airlines() {
    const fromScript = parseJsonScript(this.hasAirlinesDataTarget ? this.airlinesDataTarget : null)
    return Array.isArray(fromScript) ? fromScript : []
  }

  normalizeCodes(codes) {
    return [...new Set((codes || []).map((code) => String(code).trim().toUpperCase()).filter((code) => IATA_CODE.test(code)))]
  }

  airlineByCode(code) {
    return this.airlines.find((airline) => airline.code === code)
  }

  labelFor(code) {
    const airline = this.airlineByCode(code)
    return airline ? `${code} · ${airline.name}` : code
  }

  filterAndShow() {
    const query = (this.searchInputTarget.value || "").trim().toLowerCase()
    const selected = new Set(this.selectedValue)
    const matches = this.airlines.filter((airline) => {
      if (selected.has(airline.code)) return false
      if (!query) return true
      return [airline.code, airline.name, airline.group]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    })

    this.listTarget.innerHTML = ""
    const typedCode = (this.searchInputTarget.value || "").trim().toUpperCase()
    const canAddTyped = IATA_CODE.test(typedCode) && !selected.has(typedCode)

    if (matches.length === 0 && !canAddTyped) {
      this.listTarget.classList.add("hidden")
      return
    }

    this.listTarget.classList.remove("hidden")

    if (canAddTyped && !matches.some((airline) => airline.code === typedCode)) {
      this.appendOption(typedCode, `Add ${typedCode}`)
    }

    let lastGroup = null
    matches.slice(0, 80).forEach((airline) => {
      const group = airline.group || "Other"
      if (group !== lastGroup) {
        const header = document.createElement("div")
        header.className = "px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-base-content/50"
        header.textContent = group
        this.listTarget.appendChild(header)
        lastGroup = group
      }
      this.appendOption(airline.code, `${airline.code} · ${airline.name}`)
    })
  }

  appendOption(code, label) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "block w-full text-left px-3 py-2 text-sm hover:bg-base-200 focus:bg-base-200 focus:outline-none"
    button.textContent = label
    button.addEventListener("click", () => this.add(code))
    this.listTarget.appendChild(button)
  }

  onSearchKeydown(event) {
    if (event.key === "Escape") {
      this.listTarget.classList.add("hidden")
      this.searchInputTarget.blur()
      return
    }

    if (event.key !== "Enter") return

    event.preventDefault()
    const typedCode = (this.searchInputTarget.value || "").trim().toUpperCase()
    if (IATA_CODE.test(typedCode)) {
      this.add(typedCode)
      return
    }

    const firstOption = this.listTarget.querySelector("button")
    firstOption?.click()
  }

  closeOnClickOutside(event) {
    if (this.element.contains(event.target)) return
    this.listTarget.classList.add("hidden")
  }

  add(code) {
    const normalized = String(code).trim().toUpperCase()
    if (!IATA_CODE.test(normalized) || this.selectedValue.includes(normalized)) return
    this.selectedValue = [...this.selectedValue, normalized]
    this.renderHiddenInputs()
    this.renderTags()
    this.searchInputTarget.value = ""
    this.filterAndShow()
  }

  remove(event) {
    const code = event.currentTarget.dataset.code
    this.selectedValue = this.selectedValue.filter((value) => value !== code)
    this.renderHiddenInputs()
    this.renderTags()
    this.filterAndShow()
  }

  renderHiddenInputs() {
    const container = this.hiddenContainerTarget
    container.innerHTML = ""
    if (this.selectedValue.length === 0) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.inputNameValue
      input.value = ""
      container.appendChild(input)
      return
    }

    this.selectedValue.forEach((code) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.inputNameValue
      input.value = code
      container.appendChild(input)
    })
  }

  renderTags() {
    const container = this.tagsTarget
    container.innerHTML = ""
    this.selectedValue.forEach((code) => {
      const wrap = document.createElement("span")
      wrap.className = "inline-flex items-center gap-1 rounded-full bg-base-100 border border-base-300 px-2.5 py-0.5 text-sm"
      const button = document.createElement("button")
      button.type = "button"
      button.className = "hover:text-error focus:outline-none"
      button.textContent = "×"
      button.dataset.code = code
      button.setAttribute("aria-label", `Remove ${code}`)
      button.addEventListener("click", (event) => this.remove(event))
      wrap.appendChild(document.createTextNode(this.labelFor(code)))
      wrap.appendChild(button)
      container.appendChild(wrap)
    })
  }
}
